import type React from 'react'
import * as Mtds from '@mattilsynet/design/react'
import {
  AnalysisType,
  PayloadSchema,
  Result,
  SubSample
} from '../../types/types'
import { SampleStatus } from '../../utils/sampleUtils'
import {
  CheckCircleIcon,
  HourglassIcon,
  ProhibitIcon,
  WarningCircleIcon,
  WarningOctagonIcon
} from 'prove-common'
import { AnalysisResultsSection } from '../sample-screen-sections/AnalysisResultsSection'
import { FilledOutByInspectorSection } from '../sample-screen-sections/FilledOutByInspectorSection'
import { retrieveSchema, RJSFSchema } from '@rjsf/utils'
import validator from '@rjsf/validator-ajv8'
import definitions from '../../../../../../schemas/definitions.json'
import conditionals from '../../../../../../schemas/conditionals.json'

const schemaDefinitions = definitions as RJSFSchema['definitions']
const schemaConditionals = conditionals as RJSFSchema

type SubSampleItemProps = {
  sampleId: string
  requisitionId: number
  subSample: SubSample
  analysisType: AnalysisType
  result: Result | undefined
  complete: boolean | undefined
  payloadSchema: PayloadSchema | undefined
  sampleStatus: SampleStatus
}

export function SubSampleStatusDisplay({
  result,
  isCompromised,
  complete,
  sampleStatus
}: Readonly<{
  result: Result | undefined
  isCompromised: boolean | undefined
  complete: boolean | undefined
  sampleStatus?: SampleStatus
}>) {
  const resultSets = result?.resultSets ?? []
  const hasPositive = resultSets.some((rs) => rs.status === 'POSITIVE')
  const hasInconclusive = resultSets.some((rs) => rs.status === 'UNDETERMINED')
  const allNegative =
    resultSets.length > 0 && resultSets.every((rs) => rs.status === 'NEGATIVE')

  if (isCompromised) {
    return (
      <span className='flex items-center gap-2'>
        <ProhibitIcon size={22} />
        <span className='font-semibold text-text-default'>Kansellert</span>
      </span>
    )
  }

  if (hasPositive) {
    return (
      <span data-color='danger' className='flex items-center gap-2'>
        <WarningOctagonIcon size={22} color='var(--mtds-color-base-default)' />
        <span className='font-semibold text-danger-base-default'>Påvist</span>
      </span>
    )
  }

  if (allNegative) {
    return (
      <span className='flex items-center gap-2'>
        <CheckCircleIcon
          size={22}
          color='var(--mtds-color-main-base-default)'
        />
        <span className='font-semibold text-text-default'>Ikke påvist</span>
      </span>
    )
  }

  if (hasInconclusive) {
    const waitingForAnalysis =
      sampleStatus != null
        ? ![
            SampleStatus.ready_to_process,
            SampleStatus.completed,
            SampleStatus.closed
          ].includes(sampleStatus)
        : !complete
    return (
      <span className='flex items-center gap-2'>
        {waitingForAnalysis ? (
          <HourglassIcon size={22} />
        ) : (
          <WarningCircleIcon size={22} />
        )}
        <span className='font-semibold text-text-default'>
          {waitingForAnalysis
            ? 'Avventer ferdig analyse'
            : 'Resultat må vurderes'}
        </span>
      </span>
    )
  }

  if (complete) {
    return (
      <Mtds.Flex
        as='span'
        data-color='warning'
        data-align='center'
        data-gap='2'
        data-nowrap
      >
        <WarningCircleIcon color='var(--mtds-color-base-default)' />
        <span>Mangler resultater</span>
      </Mtds.Flex>
    )
  }

  return (
    <span className='flex items-center gap-2'>
      <HourglassIcon size={22} color='var(--mtds-color-text-subtle)' />
      <span className='font-semibold text-text-subtle'>Venter på analyse</span>
    </span>
  )
}

export const SubSampleItem = ({
  sampleId,
  requisitionId,
  subSample,
  analysisType,
  result,
  complete,
  payloadSchema,
  sampleStatus
}: SubSampleItemProps) => {
  const schema = payloadSchema?.schema ?? '{}'
  const subSamplesSchema = JSON.parse(schema).properties?.subSamples
  const rootSchema: RJSFSchema = {
    definitions: schemaDefinitions,
    conditionals: schemaConditionals,
    type: 'object',
    properties: {
      subSamples: subSamplesSchema
    },
    required: []
  }
  const retrievedSchema = retrieveSchema(
    validator,
    subSamplesSchema,
    rootSchema
  )
  const subSamplesPayloadSchema = retrievedSchema?.items?.properties?.payload
  const hasInconclusive = result?.resultSets?.some(
    (rs) => rs.status === 'UNDETERMINED'
  )
  return (
    <Mtds.Details
      data-variant='card'
      open={hasInconclusive}
      style={
        {
          '--mtdsc-details-background': 'var(--mtds-color-background-tinted)'
        } as React.CSSProperties
      }
    >
      <Mtds.Details.Summary>
        <span className='flex items-center gap-5'>
          <span className='font-mono text-text-default shrink-0 min-w-[3ch]'>
            {subSample.subSampleId}
          </span>
          <SubSampleStatusDisplay
            result={result}
            isCompromised={subSample.compromised}
            sampleStatus={sampleStatus}
            complete={
              complete || sampleStatus === SampleStatus.ready_to_process
            }
          />
        </span>
      </Mtds.Details.Summary>
      <Mtds.Grid data-gap='6' className='mt-4'>
        {subSample.compromised && (
          <Mtds.Alert data-color='warning'>
            <Mtds.Grid data-gap='3'>
              <Mtds.Grid data-gap='1'>
                <Mtds.Heading as='h3' data-size='xs'>
                  Grunn til kansellering
                </Mtds.Heading>
                <p>{subSample.compromised_reason}</p>
              </Mtds.Grid>
              {(result?.resultSets?.length ?? 0) > 0 && (
                <Mtds.Muted>
                  Analyseresultatene beholdes, men tas ikke med i den samlede
                  vurderingen av prøven fordi delprøven er kansellert.
                </Mtds.Muted>
              )}
            </Mtds.Grid>
          </Mtds.Alert>
        )}
        <FilledOutByInspectorSection
          compact
          schema={JSON.stringify(subSamplesPayloadSchema)}
          currentPayload={subSample.payload}
        />
        <AnalysisResultsSection
          sampleId={sampleId}
          requisitionId={requisitionId}
          analysisType={analysisType}
          result={result}
          sampleStatus={sampleStatus}
          isCompromised={subSample.compromised}
        />
      </Mtds.Grid>
    </Mtds.Details>
  )
}
